import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReactComponent as ListLayoutIcon } from '@/assets/images/icons/layout/list.svg'
import { ReactComponent as SelectorIcon } from '@/assets/images/icons/arrows/chevron-selector-vertical.svg'
import { Permission } from 'types/Permission'
import { usePermissionCheck } from '@/hooks'
import Button from '@/ui/Button'
import Text from '@/ui/Text'
import Popup from '@/ui/Popup'
import { ButtonsWrapper } from './Styles'

import ButtonWithChildItems from './ButtonWithChildItems'

export interface BulkItemProps {
  content: string
  icon: ReactNode
  onClick: () => void
  requiredRoles?: Permission[]
  disabled?: boolean
  disabledReason?: string
  childItems?: BulkItemProps[]
}

interface BulkMenuItemsProps {
  fixed: boolean
  items: BulkItemProps[]
  disabled: boolean
  count: number
}

const BulkMenu = ({
  fixed,
  items = [],
  disabled = false,
  count,
}: BulkMenuItemsProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { hasPermissions } = usePermissionCheck()

  return (
    <div>
      <Popup
        flowing
        position="top left"
        on="click"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        disabled={disabled}
        trigger={
          <ButtonsWrapper fixed={fixed}>
            <Button.Group packTightly>
              <Button
                disabled={disabled}
                leftIcon={<ListLayoutIcon />}
                content={`${t('With selected')}${
                  count > 0 ? ` (${count})` : ''
                }:`}
                onClick={() => setIsOpen(!isOpen)}
              />
              <Button
                variant="outlined"
                style={{ width: '40px' }}
                fluid
                disabled={disabled}
                leftIcon={<SelectorIcon />}
                onClick={() => setIsOpen(!isOpen)}
              />
            </Button.Group>
          </ButtonsWrapper>
        }
        content={
          <Button.Group
            vertical
            packTightly
            style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {items.length === 0 && (
              <Text.Span>{t('No actions available')}</Text.Span>
            )}
            {items.map(
              ({
                content,
                icon,
                onClick,
                requiredRoles,
                disabled: bulkDisabled,
                disabledReason,
                childItems,
              }) => {
                if (!hasPermissions(requiredRoles)) {
                  return null
                }
                if (childItems && childItems.length > 0) {
                  return (
                    <ButtonWithChildItems
                      key={`${content}-${icon}`}
                      closeParent={() => setIsOpen(false)}
                      content={content}
                      icon={icon}
                      childItems={childItems}
                    />
                  )
                }
                return (
                  <Button
                    variant="outlined"
                    disabled={disabled || bulkDisabled}
                    disabledReason={disabledReason}
                    key={`${content}-${icon}`}
                    content={content}
                    leftIcon={icon}
                    onClick={() => {
                      setIsOpen(false)
                      onClick()
                    }}
                  />
                )
              },
            )}
          </Button.Group>
        }
      />
    </div>
  )
}

export default BulkMenu
